'use client';

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export type BarcodeScannerStatus = 'idle' | 'starting' | 'scanning' | 'paused' | 'error';

/**
 * - `insecure` : page servie hors HTTPS (ex. `http://*.nip.io`), le navigateur masque la caméra ;
 * - `unsupported` : navigateur sans `getUserMedia` ;
 * - `denied` : autorisation refusée (ou bloquée par le navigateur) ;
 * - `no_camera` : aucune caméra, ou aucune ne correspond ;
 * - `busy` : caméra déjà utilisée par une autre application ;
 * - `decoder_unavailable` : le décodeur de QR n'a pas pu se charger.
 */
export type BarcodeScannerError =
  | 'insecure'
  | 'unsupported'
  | 'denied'
  | 'no_camera'
  | 'busy'
  | 'decoder_unavailable'
  | 'unknown';

type DetectedBarcodeLike = { rawValue: string };
type BarcodeDetectorLike = { detect: (source: HTMLVideoElement) => Promise<DetectedBarcodeLike[]> };
type BarcodeDetectorConstructor = {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats: () => Promise<readonly string[]>;
};
type PermissionsPolicyLike = { allowsFeature?: (feature: string) => boolean };

/** Cadence de décodage : assez pour un scan instantané, sans saturer un téléphone d'entrée de gamme. */
const DETECT_INTERVAL_MS = 150;
/** Un même contenu relu dans ce délai est ignoré (le QR reste dans le champ de la caméra). */
const SAME_VALUE_COOLDOWN_MS = 3000;
const QR_FORMATS = ['qr_code'];

/**
 * La page peut-elle ouvrir la caméra ? Faux hors HTTPS, sans `getUserMedia`, ou quand la page (ou le
 * site qui embarque le widget) interdit la caméra par `Permissions-Policy`. Seuls les navigateurs
 * Chromium exposent cette politique ; ailleurs, un refus n'apparaît qu'à l'ouverture (`denied`).
 * À appeler après le montage : `window` est absent au rendu serveur.
 */
export function isCameraScanAvailable(): boolean {
  if (typeof window === 'undefined' || !window.isSecureContext) return false;
  if (!navigator.mediaDevices?.getUserMedia) return false;
  const { permissionsPolicy, featurePolicy } = document as Document & {
    permissionsPolicy?: PermissionsPolicyLike;
    featurePolicy?: PermissionsPolicyLike;
  };
  return (permissionsPolicy ?? featurePolicy)?.allowsFeature?.('camera') ?? true;
}

/**
 * Détecteur natif du navigateur quand il lit les QR (Chrome Android / macOS / ChromeOS), sinon le
 * polyfill `barcode-detector` (moteur WASM chargé à la première lecture). Importé à la demande :
 * rien n'est chargé tant que le scanner n'est pas ouvert.
 */
const createDetector = async (forcePolyfill: boolean): Promise<BarcodeDetectorLike> => {
  const native = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
  if (!forcePolyfill && native) {
    try {
      const formats = await native.getSupportedFormats();
      if (formats.includes('qr_code')) return new native({ formats: QR_FORMATS });
    } catch {
      // Détecteur natif présent mais inutilisable (« service unavailable » sur certains Android).
    }
  }
  const { BarcodeDetector } = await import('barcode-detector/ponyfill');
  return new BarcodeDetector({ formats: ['qr_code'] });
};

const toScannerError = (error: unknown): BarcodeScannerError => {
  switch ((error as { name?: string } | null)?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'no_camera';
    case 'NotReadableError':
    case 'AbortError':
      return 'busy';
    default:
      return 'unknown';
  }
};

/**
 * Caméra arrière + décodage de QR pour un `<video>` fourni par l'appelant. La caméra n'est demandée
 * que quand `active` passe à `true` (donc après un geste de l'utilisateur), et ses pistes sont
 * arrêtées dès que `active` repasse à `false`, au démontage, quand l'onglet est masqué ou quand la
 * piste s'arrête : dans ces deux derniers cas le statut passe à `paused` et `restart()` relance.
 *
 * `onDecoderLoadError` reçoit l'échec de chargement du polyfill et renvoie `true` s'il a pris la main
 * (rechargement de la page pendant un déploiement) : le statut passe alors à `unknown` plutôt qu'à
 * `decoder_unavailable`. Absent, rien n'est rechargé (tunnel public : la saisie en cours serait perdue).
 */
export function useBarcodeScanner({
  active,
  onDetected,
  onDecoderLoadError,
}: {
  active: boolean;
  onDetected: (rawValue: string) => void;
  onDecoderLoadError?: (error: unknown) => boolean;
}): {
  videoRef: RefObject<HTMLVideoElement | null>;
  status: BarcodeScannerStatus;
  error: BarcodeScannerError | null;
  restart: () => void;
} {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<BarcodeScannerStatus>('idle');
  const [error, setError] = useState<BarcodeScannerError | null>(null);
  const [attempt, setAttempt] = useState<number>(0);
  const onDetectedRef = useRef(onDetected);
  const onDecoderLoadErrorRef = useRef(onDecoderLoadError);

  useEffect((): void => {
    onDetectedRef.current = onDetected;
    onDecoderLoadErrorRef.current = onDecoderLoadError;
  }, [onDetected, onDecoderLoadError]);

  useEffect((): (() => void) | undefined => {
    if (!active) {
      setStatus('idle');
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let stream: MediaStream | null = null;
    let timerId: number | null = null;
    let detecting = false;
    let usingPolyfill = false;
    let detector: BarcodeDetectorLike | null = null;
    let lastValue: { value: string; at: number } | null = null;

    const stop = (): void => {
      if (timerId !== null) window.clearInterval(timerId);
      timerId = null;
      stream?.getTracks().forEach((track): void => track.stop());
      stream = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };

    const fail = (scannerError: BarcodeScannerError): void => {
      stop();
      if (cancelled) return;
      setError(scannerError);
      setStatus('error');
    };

    const failDecoderLoad = (loadError: unknown): void => {
      fail(onDecoderLoadErrorRef.current?.(loadError) ? 'unknown' : 'decoder_unavailable');
    };

    const pause = (): void => {
      stop();
      if (cancelled) return;
      setStatus('paused');
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden' && stream) pause();
    };

    const tick = (): void => {
      const video = videoRef.current;
      if (detecting || !detector || !video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
      detecting = true;
      detector
        .detect(video)
        .then((barcodes): void => {
          if (cancelled) return;
          const value = barcodes.find((barcode): boolean => barcode.rawValue.length > 0)?.rawValue;
          if (!value) return;
          const now = Date.now();
          if (lastValue && lastValue.value === value && now - lastValue.at < SAME_VALUE_COOLDOWN_MS) return;
          lastValue = { value, at: now };
          onDetectedRef.current(value);
        })
        .catch(async (detectError: unknown): Promise<void> => {
          if (cancelled) return;
          if (usingPolyfill) {
            failDecoderLoad(detectError);
            return;
          }
          // Le détecteur natif peut exister et échouer à l'usage : on bascule une fois sur le polyfill.
          usingPolyfill = true;
          try {
            detector = await createDetector(true);
          } catch (importError: unknown) {
            failDecoderLoad(importError);
          }
        })
        .finally((): void => {
          detecting = false;
        });
    };

    const start = async (): Promise<void> => {
      setError(null);
      setStatus('starting');

      if (typeof window === 'undefined' || !window.isSecureContext) {
        fail('insecure');
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        fail('unsupported');
        return;
      }

      try {
        detector = await createDetector(false);
      } catch (importError: unknown) {
        // Pendant un déploiement, le chunk du décodeur peut avoir disparu.
        failDecoderLoad(importError);
        return;
      }
      if (cancelled) return;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          // `ideal` et non `exact` : un ordinateur n'a souvent qu'une caméra frontale.
          video: { facingMode: { ideal: 'environment' } },
        });
      } catch (mediaError: unknown) {
        fail(toScannerError(mediaError));
        return;
      }
      if (cancelled) {
        stop();
        return;
      }

      stream.getVideoTracks().forEach((track): void => track.addEventListener('ended', pause));
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play().catch((): void => undefined);
      }
      if (cancelled) {
        stop();
        return;
      }
      setStatus('scanning');
      timerId = window.setInterval(tick, DETECT_INTERVAL_MS);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    void start();

    return (): void => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stop();
    };
  }, [active, attempt]);

  const restart = useCallback((): void => {
    setAttempt((value): number => value + 1);
  }, []);

  return { videoRef, status, error, restart };
}
